/**
 * SiteShell.jsx — the shared chrome of personadraft.com (website UI handoff).
 *
 *   <SiteNav/>, <SiteFooter/>, <FinalCta/>, <CtaBand/>, <SiteLink/>, <PMark/>,
 *   <VoiceDots/>, <Faq/>
 *   plus the voice catalogue every page reads and the auth hook the nav uses.
 *
 * Every page renders inside <div className="pd-site"> (site.css). SSR-safe:
 * nothing here touches window during render; the auth state is read in effects.
 */
import { useEffect, useState } from 'react';
import { navTo } from '../utils/navigate.js';
import { supabase } from '../utils/supabase.js';
import { useRoutePath } from '../utils/routePath.js';
import { SURFACES, isLive, CTA_SUB, WAITLIST_PATH } from './siteConfig.js';

export const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/persona/oppecdkehgmdjcnlkohlombfdlfjfeff';
export const SUPPORT_EMAIL = 'support@personadraft.com';
export const SITE_URL = 'https://personadraft.com';

// The eight voices — id (engine attractor), single-word name, descriptor, colour.
export const VOICES = [
  { id: 'me',             name: 'Me',        desc: 'your fingerprint', color: '#0F1420' },
  { id: 'the_executive',  name: 'Direct',    desc: 'decisive',         color: '#E2455A' },
  { id: 'the_catalyst',   name: 'Bold',      desc: 'energizing',       color: '#F2762B' },
  { id: 'the_diplomat',   name: 'Gentle',    desc: 'considerate',      color: '#3D7DE0' },
  { id: 'the_analyst',    name: 'Precise',   desc: 'data-led',         color: '#1BB8C4' },
  { id: 'the_facilitator',name: 'Steady',    desc: 'organized',        color: '#2FB46B' },
  { id: 'the_veteran',    name: 'Casual',    desc: 'quick',            color: '#9B5DE0' },
  { id: 'the_visionary',  name: 'Visionary', desc: 'big picture',      color: '#D8519E' },
];
export const VOICE_BY_ID = Object.fromEntries(VOICES.map((v) => [v.id, v]));

// The eight dials, in the order the site shows them.
export const DIALS = [
  { id: 'command',    label: 'Assertiveness' },
  { id: 'warmth',     label: 'Warmth' },
  { id: 'stability',  label: 'Urgency' },
  { id: 'economy',    label: 'Brevity' },
  { id: 'unpolished', label: 'Formality' },
  { id: 'humanity',   label: 'Expressiveness' },
  { id: 'rigor',      label: 'Detail' },
  { id: 'candidness', label: 'Tact' },
];

export function PMark({ size = 24, white = false, dot = true }) {
  const ink = white ? '#fff' : '#0F1420';
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} fill="none" aria-hidden="true">
      <path d="M26 18V82h36" stroke={ink} strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M26 18h22a19 19 0 0 1 0 38H26" stroke={ink} strokeWidth="13" strokeLinecap="round" strokeLinejoin="round" />
      {dot && <circle cx="78" cy="82" r="9" fill="#F28C1E" />}
    </svg>
  );
}

export function VoiceDots({ large = false }) {
  return (
    <span className={`s-dots${large ? ' lg' : ''}`} aria-hidden="true">
      {VOICES.map((v) => <i key={v.id} style={{ background: v.color }} />)}
    </span>
  );
}

export function GoogleG({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.4 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v8.1h12.7c-.3 2.1-1.6 5.3-4.7 7.4l7.3 5.6c4.4-4 7.2-10 7.2-17.1z" />
      <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.7 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.6c-2 1.4-4.7 2.4-8.6 2.4-6.3 0-11.6-4-13.5-9.7l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}
export function MicrosoftM({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true">
      <rect width="9" height="9" fill="#F25022" /><rect x="11" width="9" height="9" fill="#7FBA00" />
      <rect y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

/** Current Supabase user, read in an effect (null on the server and until known). */
export function useAuthUser() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!supabase) { setReady(true); return undefined; }
    let alive = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!alive) return;
      setUser(session?.user || null); setReady(true);
    }).catch(() => { if (alive) setReady(true); });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (alive) setUser(session?.user || null);
    });
    return () => { alive = false; subscription?.unsubscribe(); };
  }, []);
  return { user, ready };
}

/** SPA navigation from a real <a>: crawlable + middle-clickable, no reload on click. */
export const spa = (href) => (e) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
  if (href.startsWith('#') || href.startsWith('/#')) return; // let the browser scroll
  e.preventDefault();
  navTo(href);
  // A new page starts at its top; a #section target is scrolled to by that page.
  if (!href.includes('#')) window.scrollTo(0, 0);
};

/**
 * A link to the page you are already on scrolls instead of reloading it: on
 * /how-it-works "How it works" is #act1, on / "For Teams" is #teams.
 */
export function hereHref(href, path) {
  if (href === '/how-it-works' && path === '/how-it-works') return '#act1';
  if (href.startsWith('/#') && path === '/') return href.slice(1);
  return href;
}

/** One <a> for every kind of href: SPA for internal routes, new tab only for the outside web. */
export function SiteLink({ href, children, ...rest }) {
  if (/^https?:/.test(href)) return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>;
  if (href.startsWith('mailto:') || href.startsWith('#')) return <a href={href} {...rest}>{children}</a>;
  return <a href={href} onClick={spa(href)} {...rest}>{children}</a>;
}

// For Teams points at the home band until the Teams page ships.
const NAV_LINKS = [
  ['How it works', '/how-it-works'], ['For Teams', '/#teams'], ['Pricing', '/pricing'], ['Security', '/privacy'], ['Blog', '/blog'],
];

export function SiteNav({ minimal = false }) {
  const path = useRoutePath();
  const { user } = useAuthUser();
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [path]);
  const initial = ((user?.user_metadata?.full_name || user?.email || '?').trim()[0] || '?').toUpperCase();
  const active = (href) => (href.startsWith('/#') ? false : path === href || (href !== '/' && path.startsWith(href + '/')));
  return (
    <nav className="s-nav">
      <div className="s-wrap">
        <div className="s-nav-in">
          <a className="s-logo" href="/" onClick={spa('/')} aria-label="PersonaDraft home"><PMark size={24} />PersonaDraft</a>
          {minimal ? (
            <span className="s-nav-help">Need help? <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></span>
          ) : (
            <>
              <div className="s-nav-links">
                {NAV_LINKS.map(([label, href]) => (
                  <SiteLink key={href} href={hereHref(href, path)} className={active(href) ? 'on' : undefined} aria-current={active(href) ? 'page' : undefined}>{label}</SiteLink>
                ))}
              </div>
              <div className="s-nav-right">
                {user
                  ? <button type="button" className="s-nav-av" onClick={() => navTo('/account')} aria-label="Account">{initial}</button>
                  : <a className="s-nav-signin" href="/signin" onClick={spa('/signin')}>Sign in</a>}
                <a className="s-btn nav" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome, free.</a>
                <button type="button" className="s-burger" aria-label="Menu" aria-expanded={open} onClick={() => setOpen((v) => !v)}><span /><span /></button>
              </div>
            </>
          )}
        </div>
        {!minimal && (
          <div className={`s-nav-mobile${open ? ' open' : ''}`}>
            {NAV_LINKS.map(([label, href]) => <SiteLink key={href} href={hereHref(href, path)} onClickCapture={() => setOpen(false)}>{label}</SiteLink>)}
            <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome, free.</a>
          </div>
        )}
      </div>
    </nav>
  );
}

const FOOT = [
  { title: 'Product', links: [['How it works', '/how-it-works'], ['Pricing', '/pricing'], ['Security & Trust', '/privacy'], ['For Teams', '/#teams']] },
  // Live surfaces install from the Web Store; the rest are a waitlist (siteConfig.js).
  { title: 'Surfaces', links: SURFACES.map((s) => (isLive(s.key) ? [s.name, CHROME_STORE_URL] : [`${s.name} · waitlist`, WAITLIST_PATH])) },
  { title: 'For', links: [['Non-native English', '/for/non-native-english'], ['Sales', '/for/sales'], ['Executives', '/for/executives'], ['Recruiters', '/for/recruiters']] },
  { title: 'Compare', links: [['vs Grammarly', '/vs/grammarly'], ['vs Gemini', '/vs/gemini'], ['vs ChatGPT', '/vs/chatgpt-for-email'], ['vs Lavender', '/vs/lavender']] },
  // How to Say It has no index page yet, so it opens on one intent.
  { title: 'Resources', links: [['Blog', '/blog'], ['How to Say It', '/how-to/say-no-to-a-request'], ['Privacy', '/privacy'], ['Terms', '/terms']] },
];

export function SiteFooter({ simple = false, also = null }) {
  const path = useRoutePath();
  if (simple) {
    return (
      <div className="s-wrap">
        <div className="s-foot-simple">
          <span>© 2026 PersonaDraft</span>
          <span>{also || <><a href="/privacy" onClick={spa('/privacy')}>Privacy</a> · <a href="/terms" onClick={spa('/terms')}>Terms</a> · <a href={`mailto:${SUPPORT_EMAIL}`}>Contact</a></>}</span>
        </div>
      </div>
    );
  }
  return (
    <footer className="s-foot2">
      <div className="s-w2">
        <div className="s-foot2-grid">
          <div className="s-foot2-brand">
            <div className="s-logo"><PMark size={22} />PersonaDraft</div>
            <p>A behavioral engine that maps your voice, learns it, and writes as you — on every surface.</p>
          </div>
          {FOOT.map((g) => (
            <div key={g.title} className="s-foot2-col">
              <b>{g.title}</b>
              {g.links.map(([label, href]) => <SiteLink key={label} href={hereHref(href, path)}>{label}</SiteLink>)}
            </div>
          ))}
        </div>
        <div className="s-foot2-copy">© 2026 PersonaDraft</div>
      </div>
    </footer>
  );
}

/** The closing band on the home and How It Works pages; its sub-line is the honesty line. */
export function FinalCta() {
  return (
    <section className="s-final">
      <div className="s-w2">
        <div className="s-final-card">
          <div className="s-final-copy">
            <h2>Sound like yourself, everywhere.</h2>
            <p>{CTA_SUB}</p>
          </div>
          <a className="s-btn xl" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">Add to Chrome, free.</a>
        </div>
      </div>
    </section>
  );
}

export function CtaBand({ title, sub, label = 'Add to Chrome — free', md = false }) {
  return (
    <div className="s-wrap">
      <div className={`s-cta${md ? ' md' : ''}`}>
        <div>
          <h2>{title}</h2>
          {sub && <p>{sub}</p>}
        </div>
        <a className="s-btn xl" href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer">{label}</a>
      </div>
    </div>
  );
}

/** FAQ accordion: one open at a time, +/– glyph. `a` may contain trusted inline HTML. */
export function Faq({ items, openFirst = true }) {
  const [open, setOpen] = useState(openFirst ? 0 : -1);
  return (
    <div className="s-faq">
      {items.map((f, i) => {
        const on = open === i;
        return (
          <div className="s-faq-item" key={i}>
            <div>
              <button type="button" className="s-faq-q" aria-expanded={on} onClick={() => setOpen(on ? -1 : i)}>{f.q}</button>
              {on && <p className="s-faq-a" dangerouslySetInnerHTML={{ __html: f.a }} />}
            </div>
            <button type="button" className="s-faq-glyph" aria-label={on ? 'Collapse' : 'Expand'} onClick={() => setOpen(on ? -1 : i)}>{on ? '–' : '+'}</button>
          </div>
        );
      })}
    </div>
  );
}

/** Dial rows: values 0–100 per dial id; `voiceColor` paints the voice, `moved` the one dial the moment moved (orange). */
export function DialRows({ values, voiceColor = '#0F1420', moved = null, selected = null, onChange = null, big = false, caption = null }) {
  return (
    <div className="s-dials">
      {DIALS.map((d) => {
        const v = Math.max(0, Math.min(100, Math.round(values[d.id] ?? 50)));
        const isMoved = moved === d.id;
        const isSel = selected === d.id;
        const color = isMoved ? '#F28C1E' : (isSel ? '#F28C1E' : voiceColor);
        return (
          <div className={`s-dial${isSel ? ' sel' : ''}`} key={d.id}>
            <span>{d.label}</span>
            <span className={`s-track${big && isSel ? ' big' : ''}`}>
              <i style={{ width: `${v}%`, background: (isSel || isMoved) ? '#F28C1E' : voiceColor }} />
              <b style={{ left: `${v}%`, background: color }} />
              {onChange && <input className="s-range" type="range" min="0" max="100" value={v} aria-label={d.label} onChange={(e) => onChange(d.id, Number(e.target.value))} />}
            </span>
            <span className="s-dial-v" style={isMoved || isSel ? { color: '#F28C1E' } : undefined}>{v}</span>
          </div>
        );
      })}
      {caption && <div className="s-dial-cap">{caption}</div>}
    </div>
  );
}
